import React, { useMemo, useCallback } from "react";
import { Row, Cell } from "@tanstack/react-table";
import { Issue } from "@adsk/acc-issues-types";
import { TableCell } from "@adsk/alloy-react-table";
import {
  StyledTableRow,
  StickyTableCell,
  tableCellForRowStyle,
} from "./issuesTableStyles";
import { SELECT_COLUMN, ACTIONS_COLUMN } from "./columnKeys";

export interface IssuesTableRowProps {
  row: Row<Issue>;
  onRowClick: (params: { rowKey: string; rowData: Issue }) => void;
  getTableRowProps: (row: Row<Issue>) => Record<string, any>;
  getTableCellProps: (cell: Cell<Issue, unknown>) => Record<string, any>;
  tableCellStyle: React.CSSProperties;
  columnSizing: Record<string, number>;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  // Type comes from `@tanstack/react-table`, `columnSizingInfo.isResizingColumn`.
  resizingColumn: false | string;
  isSelected: boolean;
  isChecked: boolean;
}

const IssuesTableRowComponent = ({
  row,
  onRowClick,
  getTableRowProps,
  getTableCellProps,
  tableCellStyle,
  columnSizing,
  columnVisibility,
  columnOrder,
  resizingColumn,
  isSelected,
  isChecked,
}: IssuesTableRowProps) => {
  const rowId = row.id;
  const rowData = row.original;
  const baseRowProps = getTableRowProps(row);

  const cellStyle = useMemo(
    () => ({
      ...tableCellStyle,
      ...tableCellForRowStyle,
    }),
    [tableCellStyle]
  );

  const stickyLeftStyle = useMemo(
    () => ({
      left: 0,
      ...tableCellStyle,
      borderBottom: tableCellForRowStyle.borderBottom,
      backgroundColor: tableCellForRowStyle.backgroundColor,
      zIndex: 1,
    }),
    [tableCellStyle]
  );

  const stickyRightStyle = useMemo(
    () => ({
      right: 0,
      borderBottom: tableCellForRowStyle.borderBottom,
      backgroundColor: tableCellForRowStyle.backgroundColor,
      zIndex: 1,
    }),
    []
  );

  const getStickyCellStyle = useCallback(
    (isActionsColumn: boolean) =>
      isActionsColumn ? stickyRightStyle : stickyLeftStyle,
    [stickyLeftStyle, stickyRightStyle]
  );

  const handleRowClick = useCallback(() => {
    onRowClick({ rowKey: rowId, rowData });
  }, [onRowClick, rowId, rowData]);

  const visibleCellsData = useMemo(() => {
    return row.getVisibleCells().map(cell => {
      const width =
        columnSizing[cell.column.id] ?? cell.column.columnDef.size ?? 150;
      return { cell, width };
    });
    // Dependencies include columnVisibility and columnOrder because Visible Cells data
    // depends on these values to determine which cells to return. When these
    // change, we need to recalculate the visible cells.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, columnSizing, columnVisibility, columnOrder, resizingColumn]);

  return (
    <StyledTableRow
      // e.g. row-e2606891-e081-4c84-85f0-5fa41f3dc36d (UUID)
      data-testid={`row-${rowId}`}
      {...baseRowProps}
      isSelected={isSelected}
      isChecked={isChecked}
      onClick={handleRowClick}
    >
      {visibleCellsData.map(({ cell, width: columnWidth }) => {
        const { column } = cell;
        const isActionsColumn = column.id === ACTIONS_COLUMN;
        const isSelectColumn = column.id === SELECT_COLUMN;
        const cellTestId = `cell-${column.id}`;
        const cellProps = getTableCellProps(cell) || {};

        const isStickyColumn = isActionsColumn || isSelectColumn;

        const sizeStyles = isStickyColumn
          ? {
              width: `${columnWidth}px`,
              minWidth: `${columnWidth}px`,
              maxWidth: `${columnWidth}px`,
            }
          : {
              minWidth: `${columnWidth}px`,
              flex: "1 1 auto",
            };

        if (isStickyColumn) {
          return (
            <StickyTableCell
              key={cell.id}
              {...cellProps}
              height={tableCellStyle.height}
              minHeight={tableCellStyle.minHeight}
              style={{
                ...getStickyCellStyle(isActionsColumn),
                ...sizeStyles,
              }}
              // e.g. cell-select, cell-actions
              data-testid={cellTestId}
            />
          );
        }

        return (
          <TableCell
            key={cell.id}
            {...cellProps}
            style={{
              ...cellStyle,
              ...sizeStyles,
            }}
            // e.g. cell-TITLE
            data-testid={cellTestId}
          />
        );
      })}
    </StyledTableRow>
  );
};
export const IssuesTableRow = React.memo(IssuesTableRowComponent);
